import StoryboardDataStore from "@storyboards/modules/storyboardDataStore/storyboardDataStore.service";
import User from "@storyboards/modules/services/user.service";
import { JwtHighbondClient } from "@acl-services/jwt-highbond-client";
import target from "../../../../../storyboards/target.json";
import AppConfig from "@storyboards/appConfig";

export default class QueryClient {
  static jwtClient = new JwtHighbondClient();

  static query(queryObj) {
    const isStoryboardPublicLink = User.isAnonymous();
    const querySlug = isStoryboardPublicLink ? { "X-Storyboard-Slug": StoryboardDataStore.getUid() } : {};
    // Echo Results user_ip so QS prefers X-Client-IP over leftmost XFF (GRCPRD-12711).
    // Do not send X-Forwarded-For — browsers forbid it; QS reads X-Client-IP.
    const xClientIp = window.xClientIp ? { "X-Client-IP": window.xClientIp } : {};
    const queryUrl = this.getQueryUrl();

    return this.jwtClient
      .fetchWithTokenRefresh(queryUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          ...querySlug,
          ...xClientIp,
        },
        body: JSON.stringify({
          query: queryObj.query,
          variables: queryObj.variables,
        }),
      })
      .then(response => response.json());
  }

  static getBaseUrl() {
    return window.location.href;
  }

  static getQueryUrl() {
    const baseUrl = this.getBaseUrl();
    const matchLocal = baseUrl.match(/^(http:\/\/localhost:\d+)([^#]*)/);
    const matchLocalDnsDomain =
      baseUrl.match(/^(http:\/\/localhost)([^/]*)/) ||
      baseUrl.match(/^(https?:\/\/[^\.]+)\.dev\.([^\.]+)\.([^/:]+)(:\d+)?/);

    if (matchLocal) {
      return "http://localhost:8000/graphql";
    }
    if (matchLocalDnsDomain) {
      return `https://${target.storyBoardsLocalDomain}/graphql`;
    } else {
      const match = baseUrl.match(/^(https?:[^\.]+)\.([^\.]+)\.([^/]+)/);
      if (!match) {
        throw `Unable to parse window.location.href URL query: ${location}`;
      }

      const urlSuffix = match[3];
      const [, domain, intialGovSuffix, subDomain] = urlSuffix.match(
        /^(highbond|diligentoneplatform)(-gov|-gov2|-gov3|-mil)?(-[\w-]+)?\.(?:com|mil)$/
      );
      let govSuffix = intialGovSuffix;
      if (
        domain === "diligentoneplatform" &&
        subDomain &&
        subDomain !== "-dev" &&
        subDomain !== "-staging" &&
        subDomain !== "-preprod"
      ) {
        const matchGovSuffix = subDomain.match(/(gov|gov2|gov3|mil)$/);
        govSuffix = matchGovSuffix[1];
      }

      const isGovCloud = govSuffix || urlSuffix === "highbond.mil" || urlSuffix === "diligentoneplatform.mil";

      if (subDomain === "-s3" || subDomain === "-dev") {
        /* Playground — regional hosts (e.g. storyboards-uk) use regional Query */
        const region = match[2].match(/^(results|storyboards|picasso)(-[a-z]{2})?$/);
        let regionSuffix = region && region[2] ? region[2] : "-us2";

        if (AppConfig.features.enableStranglerApi) {
          return `https://query-svc-strangler.${urlSuffix}/graphql`;
        }
        return `https://query${regionSuffix}-main.${urlSuffix}/graphql`;
      } else if (subDomain === "-s2" || subDomain === "-preprod") {
        /* Preprod */
        if (AppConfig.features.enableStranglerApi) {
          return `https://query-svc-strangler.${urlSuffix}/graphql`;
        }

        return `https://query-us-main.${urlSuffix}/graphql`;
      } else if (
        subDomain === "-s1" ||
        subDomain === "-staging" ||
        subDomain === "-staging-gov" ||
        subDomain === "-staging-mil"
      ) {
        /* Staging and staging gov cloud */
        if (isGovCloud) {
          /* Staging gov cloud */
          if (AppConfig.features.enableStranglerApi) {
            return `https://query-svc-strangler-gov.${urlSuffix}/graphql`;
          }
          return `https://query-usg-gov.${urlSuffix}/graphql`;
        } else {
          /* Staging */
          if (AppConfig.features.enableStranglerApi) {
            return `https://query-svc-strangler.${urlSuffix}/graphql`;
          }
          return `https://query-us2-main.${urlSuffix}/graphql`;
        }
      } else {
        if (isGovCloud) {
          /* Production gov cloud and gov cloud SLED */
          if (AppConfig.features.enableStranglerApi) {
            return `https://query-svc-strangler.${urlSuffix}/graphql`;
          }
          return `https://query.${urlSuffix}/graphql`;
        } else {
          /* Production regions others*/
          const region = match[2].match(/^(results|storyboards|picasso)(-..)?$/);
          let regionSuffix = region && region[2] ? region[2] : "-us";

          if (AppConfig.features.enableStranglerApi) {
            regionSuffix = regionSuffix === "-us" ? "" : regionSuffix;
            return `https://query-svc-strangler${regionSuffix}.${urlSuffix}/graphql`;
          }
          return `https://query${regionSuffix}-main.${urlSuffix}/graphql`;
        }
      }
    }
  }
}
